import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, Shield, AlertTriangle, Mail, ExternalLink } from "lucide-react";
import Footer from "../components/Footer";

export default function LegalHub() {
  useEffect(() => {
    // Hide Emergent copilot on public Legal Hub page
    const copilotWidget = document.querySelector('#emergent-copilot, [data-emergent-copilot], .emergent-chat-widget');
    if (copilotWidget) {
      copilotWidget.style.display = 'none';
    }
    
    return () => {
      if (copilotWidget) {
        copilotWidget.style.display = '';
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
      <div className="container mx-auto px-6 py-12 max-w-5xl flex-1">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold mb-4">Legal Hub</h1>
          <p className="text-zinc-400 text-lg">
            Important legal information, policies, and compliance documentation
          </p>
        </div>

        {/* Main Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Terms of Service */}
          <Link 
            to="/terms"
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 hover:border-blue-500 transition group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-blue-900/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900/40 transition">
                <FileText size={32} className="text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold mb-3">Terms of Service</h2>
              <p className="text-zinc-400 mb-4">
                Rules, guidelines, and conditions for using our platform
              </p>
              <div className="inline-flex items-center gap-2 text-blue-500 group-hover:gap-3 transition-all">
                Read Terms
                <ExternalLink size={16} />
              </div>
            </div>
          </Link>

          {/* Privacy Policy */}
          <Link 
            to="/privacy"
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 hover:border-emerald-500 transition group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-emerald-900/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-emerald-900/40 transition">
                <Shield size={32} className="text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold mb-3">Privacy Policy</h2>
              <p className="text-zinc-400 mb-4">
                How we collect, use, and protect your personal data (GDPR compliant)
              </p>
              <div className="inline-flex items-center gap-2 text-emerald-500 group-hover:gap-3 transition-all">
                Read Policy
                <ExternalLink size={16} />
              </div>
            </div>
          </Link>

          {/* DMCA Takedown */}
          <Link 
            to="/takedown"
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 hover:border-red-500 transition group"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mb-4 group-hover:bg-red-900/40 transition">
                <AlertTriangle size={32} className="text-red-500" />
              </div>
              <h2 className="text-2xl font-bold mb-3">DMCA Takedown</h2>
              <p className="text-zinc-400 mb-4">
                Submit copyright claims and content removal requests
              </p>
              <div className="inline-flex items-center gap-2 text-red-500 group-hover:gap-3 transition-all">
                File Request
                <ExternalLink size={16} />
              </div>
            </div>
          </Link>
        </div>

        {/* Quick Links */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
          <h3 className="text-xl font-bold mb-6">Quick Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a 
              href="/user-deletion"
              className="flex items-center gap-3 text-zinc-400 hover:text-white transition"
            >
              <FileText size={16} />
              Account Deletion Request
            </a>
            <a 
              href="mailto:privacy@giopilos.com"
              className="flex items-center gap-3 text-zinc-400 hover:text-white transition"
            >
              <Mail size={16} />
              Contact Privacy Team
            </a>
            <Link 
              to="/"
              className="flex items-center gap-3 text-zinc-400 hover:text-white transition"
            >
              <FileText size={16} />
              Home
            </Link>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 inline-block">
            <p className="text-zinc-300 mb-4">
              For legal inquiries, privacy concerns, or content removal requests:
            </p>
            <a 
              href="mailto:privacy@giopilos.com"
              className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition font-semibold text-lg"
            >
              <Mail size={20} />
              privacy@giopilos.com
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
